import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build-ID detection - every API response carries an X-App-Build header. The
// first response captures the baseline; any subsequent response with a
// different build ID means the backend has been redeployed. We dispatch a
// custom event that ForceRefreshBanner listens for and responds to with the
// same 30s countdown UX as the admin push-refresh flow.
//
// Why this matters: it works on EVERY API call (not just polling), so even a
// user who just clicked "Save" gets the refresh prompt within milliseconds of
// a deploy. And it's automatic - Admins don't need to remember to push refresh
// after each deploy.
let knownBuildId = null;

// ─── Token source ──────────────────────────────────────────────────────────
// Mirror the auth token in memory, not just localStorage. The request
// interceptor reads this mirror first so a momentarily unavailable or
// non-persisting localStorage (private mode, privacy/security software) can't
// send an unauthenticated request — which the backend would reject and the app
// would mistake for a logout, producing a sign-in → click → signed-out loop.
let inMemoryToken = (() => {
  try { return localStorage.getItem('token'); } catch { return null; }
})();

export const setAuthToken = (t) => {
  inMemoryToken = t || null;
  try {
    if (t) localStorage.setItem('token', t);
    else localStorage.removeItem('token');
  } catch { /* storage blocked — the in-memory mirror still authenticates this tab */ }
};

export const getAuthToken = () => {
  if (inMemoryToken) return inMemoryToken;
  try { return localStorage.getItem('token'); } catch { return null; }
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => {
    const buildId = res.headers?.['x-app-build'];
    if (buildId) {
      if (knownBuildId === null) {
        knownBuildId = buildId;
      } else if (knownBuildId !== buildId) {
        // Drift detected - fire a global event the banner subscribes to.
        // We deliberately don't reload here; banner owns the UX.
        window.dispatchEvent(new CustomEvent('app:build-drift', {
          detail: { from: knownBuildId, to: buildId },
        }));
        // Bump local known id so we only fire once per drift.
        knownBuildId = buildId;
      }
    }
    return res;
  },
  (err) => {
    const status = err.response?.status;
    // Only a genuine authentication failure (401) ends the session. A 403 means
    // "authenticated but not permitted" — never clear the token or redirect for
    // it, or users get bounced to /login the moment they touch a resource they
    // lack permission for. Each page/component handles its own 403 locally.
    if (status === 401) {
      setAuthToken(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;
